/**
 * Pure helpers for the /shopping-list page (Plan 17).
 *
 * Extracted out of `shopping-list.tsx` so unit tests can import them without
 * pulling in React + TanStack Query + the router. Same pattern as
 * `kitchen-helpers.ts` for Plan 16.
 */

export type ShoppingListTab = 'ingredients' | 'recipes' | 'deliverables'

// ---------------------------------------------------------------------------
// Ingredients grouped by USDA type (Sheila 2026-08-22)
// ---------------------------------------------------------------------------

export interface IngredientGroupableRow {
  category: 'FOOD' | 'SUPPLY' | 'UNCATEGORIZED'
  usdaCategory: string | null
}

export interface IngredientTypeGroup<T> {
  label: string
  rows: T[]
}

/** Non-USDA buckets, rendered after the alphabetical USDA categories. */
export const INGREDIENT_SUPPLY_GROUP_LABEL = 'Supplies'
export const INGREDIENT_OTHER_GROUP_LABEL = 'Other'

/**
 * Group ingredient rows by their USDA CNDB category ("Spices and Herbs",
 * "Poultry", ...), alphabetically; SUPPLY items collect under "Supplies"
 * and unmapped foods under "Other", both after the USDA categories.
 *
 * Returns null when NO row carries a USDA category — a catalog with no
 * mappings would render as one giant "Other" section, which is worse
 * than the flat list, so the caller falls back to ungrouped rendering.
 * Input order is preserved within each group (callers pre-sort rows).
 */
export function groupIngredientRowsByType<T extends IngredientGroupableRow>(
  rows: readonly T[],
): Array<IngredientTypeGroup<T>> | null {
  if (!rows.some((r) => r.usdaCategory !== null)) {
    return null
  }
  const usda = new Map<string, T[]>()
  const supplies: T[] = []
  const other: T[] = []
  for (const row of rows) {
    if (row.usdaCategory !== null) {
      const bucket = usda.get(row.usdaCategory)
      if (bucket) {
        bucket.push(row)
      } else {
        usda.set(row.usdaCategory, [row])
      }
    } else if (row.category === 'SUPPLY') {
      supplies.push(row)
    } else {
      other.push(row)
    }
  }
  const groups: Array<IngredientTypeGroup<T>> = [...usda.keys()]
    .sort((a, z) => a.localeCompare(z))
    .map((label) => ({ label, rows: usda.get(label)! }))
  if (supplies.length > 0) {
    groups.push({ label: INGREDIENT_SUPPLY_GROUP_LABEL, rows: supplies })
  }
  if (other.length > 0) {
    groups.push({ label: INGREDIENT_OTHER_GROUP_LABEL, rows: other })
  }
  return groups
}

/**
 * Resolve a legacy `?tab=` search param. Before the Aug 2026 chef-sidebar
 * split, /shopping-list hosted all three views as tabs; today
 * `ShoppingListPage` uses this only to redirect old `?tab=recipes` /
 * `?tab=deliverables` links to the standalone /recipes and /deliverables
 * pages. Anything else (missing, empty, unknown) falls back to
 * `ingredients` — the canonical no-param URL — so a stale or mistyped
 * link never lands on a blank surface.
 */
export function parseShoppingListTab(param: string | null): ShoppingListTab {
  return param === 'recipes' || param === 'deliverables' ? param : 'ingredients'
}
